import React from 'react';
import { Calendar, FileCheck, Users, Rocket } from 'lucide-react';

export const HowItWorksSection = () => {
  const steps = [
    {
      icon: Calendar,
      number: '01',
      title: 'Book Your Free Consultation',
      description: 'Schedule a 30-minute call with our team. We\'ll understand your business, current challenges, and specific needs.',
      duration: '30 mins'
    },
    {
      icon: FileCheck,
      number: '02',
      title: 'Custom Plan & Onboarding',
      description: 'We create a tailored plan for your business. Our team seamlessly takes over your finance operations with zero disruption.',
      duration: '2-3 days'
    },
    {
      icon: Users,
      number: '03',
      title: 'Meet Your Dedicated Team',
      description: 'Get introduced to your dedicated CA team who will be your partners in managing all finance and compliance matters.',
      duration: '1 day'
    },
    {
      icon: Rocket,
      number: '04',
      title: 'Relax & Focus on Growth',
      description: 'That\'s it! Your finance operations are in expert hands. Get regular updates, insights, and proactive support.',
      duration: 'Ongoing'
    }
  ];

  return (
    <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium mb-4">
            Simple Process
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Get Started in Just
            <span className="block bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              4 Simple Steps
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From first call to complete handoff—we make it incredibly easy to transform your finance operations.
          </p>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Connection Line */}
          <div className="hidden lg:block absolute top-24 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-200 via-teal-200 to-emerald-200" style={{ top: '120px', left: '10%', right: '10%' }}></div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={step.title || `step-${index}`} className="relative">
                  <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-emerald-300 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 h-full">
                    {/* Step Number */}
                    <div className="absolute -top-4 -left-4 w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center shadow-lg">
                      <span className="text-white font-bold text-lg">{step.number}</span>
                    </div>

                    {/* Icon */}
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center mb-6 mx-auto">
                      <Icon className="w-8 h-8 text-emerald-600" />
                    </div>

                    {/* Duration Badge */}
                    <div className="inline-block px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium mb-4">
                      {step.duration}
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom Note */}
        <div className="mt-16 text-center">
          <div className="inline-block p-6 rounded-2xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200">
            <p className="text-lg text-gray-700">
              <span className="font-bold text-emerald-600">Average onboarding time: 3-5 days.</span> Most clients see immediate relief from day one.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};